// Create project (spec §6.1): a project made in Hyprflow, with a key, a lead
// and the people who work on it. The rules are create_project's
// (20261016090000_create_project.sql): projects:full only, the key's format
// and uniqueness, who may join, and that without pipeline:full a deal's own
// Zoho manager becomes the lead. Its refusals are shown as written.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import { keyError, ROLE_OPTIONS, suggestKey, type ProjectRole } from '../../lib/projects';
import { Avatar, Button, IconButton, Select } from '../../components/primitives';
import { Dialog } from '../../components/dialog';
import { DateField, Field, TextInput } from '../../components/field';
import { InlineStatus, useToast } from '../../components/feedback';
import { PersonPicker } from '../../components/personPicker';
import { useProjectPeople, type EligiblePerson } from '../../lib/projectPeople';

type Row = { user_id: string; role: ProjectRole };

export function CreateProjectDialog({
  onClose,
  onFromDeal,
}: {
  onClose: () => void;
  /** The older way in: a won Zoho deal the sync has mirrored. */
  onFromDeal: () => void;
}) {
  const navigate = useNavigate();
  const { push } = useToast();
  const { session } = useAuth();
  const uid = session?.user?.id ?? null;
  const people = useProjectPeople(null);
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyTyped, setKeyTyped] = useState(false);
  const [picked, setLead] = useState<string | null>(null);
  // The lead defaults to you, once the roster says you can lead a project.
  const lead = picked ?? (uid && people?.some((p) => p.id === uid && p.can_edit) ? uid : null);
  const [members, setMembers] = useState<Row[]>([]);
  const [client, setClient] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [deadline, setDeadline] = useState('');
  const [deal, setDeal] = useState('');
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [keyTaken, setKeyTaken] = useState<string>();
  const [error, setError] = useState<string>();

  const shownKey = keyTyped ? key : name.trim() ? suggestKey(name) : '';
  const nameError = tried && !name.trim() ? 'Give the project a name.' : undefined;
  const kError = keyTaken ?? (tried || keyTyped ? keyError(shownKey) : undefined);
  const byId = new Map((people ?? []).map((p) => [p.id, p]));
  const leaders = (people ?? []).filter((p) => p.can_edit);
  const pickable = (people ?? []).filter((p) => p.id !== lead);
  const dirty =
    !busy && (name.trim() !== '' || members.length > 0 || client !== '' || deal.trim() !== '');

  const setPicked = (ids: string[]) =>
    setMembers((rows) =>
      ids.map(
        (id) =>
          rows.find((r) => r.user_id === id) ?? {
            user_id: id,
            // Someone who can only view projects can only be a Viewer.
            role: byId.get(id)?.can_edit === false ? 'viewer' : 'contributor',
          },
      ),
    );

  const submit = async () => {
    if (!supabase) return;
    setTried(true);
    setError(undefined);
    setKeyTaken(undefined);
    if (!name.trim() || keyError(shownKey) || !lead) {
      if (!lead) setError('Pick the project lead.');
      return;
    }
    setBusy(true);
    const { data, error: e } = await supabase.rpc('create_project', {
      p_name: name.trim(),
      p_key: shownKey,
      p_lead: lead,
      p_members: members.filter((m) => m.user_id !== lead),
      p_client: client.trim() || null,
      p_starts_on: startsOn || null,
      p_deadline: deadline || null,
      p_deal: deal.trim() || null,
    });
    setBusy(false);
    if (e) {
      if (/already used/i.test(e.message)) setKeyTaken(e.message);
      else setError(e.message);
      return;
    }
    push({ tone: 'success', message: `${name.trim()} is set up.` });
    onClose();
    navigate(`/project?id=${data as string}&tab=board`);
  };

  return (
    <Dialog
      open
      title="Create project"
      onClose={onClose}
      dirty={dirty}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            Create
          </Button>
        </>
      )}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        {error && <InlineStatus tone="error">{error}</InlineStatus>}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(110px, 1fr)',
            gap: 12,
          }}
        >
          <Field label="Name" required error={nameError}>
            <TextInput value={name} onChange={setName} maxLength={80} autoComplete="off" />
          </Field>
          <Field
            label="Key"
            required
            error={kError}
            hint={kError ? undefined : 'Work items read KEY-1, KEY-2…'}
          >
            <TextInput
              mono
              value={shownKey}
              onChange={(v) => {
                setKeyTyped(true);
                setKeyTaken(undefined);
                setKey(v.toUpperCase().replace(/\s+/g, ''));
              }}
              maxLength={10}
              autoComplete="off"
            />
          </Field>
        </div>

        <Field label="Project lead" required>
          {(c) => (
            <PersonPicker
              {...c}
              people={leaders}
              value={lead}
              onChange={setLead}
              placeholder={people ? 'Pick the lead' : 'Loading people…'}
            />
          )}
        </Field>

        <Field
          label="Members"
          hint="Lead and Member work on items; a Viewer can look and comment. People who can only view projects join as Viewers."
        >
          {(c) => (
            <PersonPicker
              {...c}
              multiple
              people={pickable}
              value={members.map((m) => m.user_id)}
              onChange={setPicked}
              placeholder={members.length ? `${members.length} added — add more` : 'Add people'}
            />
          )}
        </Field>
        {members.length > 0 && (
          <ul
            aria-label="Chosen members"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {members.map((m) => {
              const p: EligiblePerson | undefined = byId.get(m.user_id);
              const name = p?.name ?? 'Teammate';
              return (
                <li key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar name={name} size={26} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--hf-text-sm)' }}>
                    {name}
                  </span>
                  <span style={{ width: 130 }}>
                    <Select
                      ariaLabel={`${name}’s role`}
                      value={m.role}
                      options={
                        p?.can_edit === false
                          ? ROLE_OPTIONS.filter((o) => o.value === 'viewer')
                          : ROLE_OPTIONS
                      }
                      onChange={(v) =>
                        setMembers((rows) =>
                          rows.map((r) =>
                            r.user_id === m.user_id ? { ...r, role: v as ProjectRole } : r,
                          ),
                        )
                      }
                    />
                  </span>
                  <IconButton
                    icon="x"
                    label={`Remove ${name}`}
                    onClick={() =>
                      setMembers((rows) => rows.filter((r) => r.user_id !== m.user_id))
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}

        <Field label="Client">
          <TextInput value={client} onChange={setClient} autoComplete="off" />
        </Field>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
            gap: 12,
          }}
        >
          <DateField label="Start date" value={startsOn} onChange={setStartsOn} />
          <DateField
            label="Due date"
            value={deadline}
            onChange={setDeadline}
            error={
              startsOn && deadline && deadline < startsOn
                ? 'The due date is before the start.'
                : undefined
            }
          />
        </div>
        <Field
          label="Zoho deal ID (optional)"
          hint="If you can’t manage deals, the deal’s project manager in Zoho becomes the lead."
        >
          <TextInput mono value={deal} onChange={setDeal} inputMode="numeric" autoComplete="off" />
        </Field>
        <p style={{ margin: 0, fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
          Have a won Zoho deal?{' '}
          <button
            type="button"
            onClick={onFromDeal}
            className="hf-hit"
            style={{
              background: 'transparent',
              border: 0,
              padding: 0,
              font: 'inherit',
              color: 'var(--hf-accent-text)',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Set it up from the deal
          </button>
        </p>
      </form>
    </Dialog>
  );
}
